import { datadogRum } from '@datadog/browser-rum';
import { useTenantStore } from '../stores/useTenantStore';

/**
 * Datadog RUM user identity + tenant context.
 *
 * Parallel to `../sentry/context.ts`, and the dependency-direction decision is
 * the same: `libs/core` is the leaf — `apps/customer-portal` depends on
 * `@sera/core`, never the reverse. The auth store lives HERE in libs/core and
 * therefore CANNOT import the app's `plugins/datadog.ts`. `@datadog/browser-rum`
 * IS a root dependency reachable by libs/core, and `datadogRum.setUser` /
 * `setGlobalContextProperty` operate on the global RUM instance regardless of
 * which module called `datadogRum.init` (in the app plugin). So these helpers
 * live here, and the auth store imports them from this module.
 *
 * All helpers are safe to call when RUM is disabled (localhost/dev, or before
 * credentials are provisioned) — the SDK buffers or no-ops on an uninitialized
 * instance.
 */
export interface DatadogRumUser {
  id: number | string;
  email: string;
  username?: string;
  role?: string;
}

/**
 * Identify the current customer on the global RUM instance and stamp the tenant
 * as a global context property (the RUM analogue of Sentry's `tenant` tag).
 * Only DEFINED fields are sent: `id`/`email` always; `name` ONLY when `username`
 * is a non-empty string; `role` when present. The tenant slug is read off the
 * tenant store the same way identifyPendo/setSentryUser do.
 */
export function setDatadogRumUser(u: DatadogRumUser): void {
  const userObj: Record<string, number | string> = {
    id: u.id,
    email: u.email,
  };
  if (typeof u.username === 'string' && u.username.length > 0) {
    userObj.name = u.username;
  }
  if (u.role != null) {
    userObj.role = u.role;
  }
  datadogRum.setUser(userObj);

  const tenantStore = useTenantStore();
  const tenantSlug = tenantStore.config?.tenantSlug ?? 'unknown';
  datadogRum.setGlobalContextProperty('tenant', tenantSlug);
}

/** Clear the RUM user and the global tenant property on logout. */
export function clearDatadogRumUser(): void {
  datadogRum.clearUser();
  datadogRum.removeGlobalContextProperty('tenant');
}
